import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline';
  /** Size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Loading state - shows spinner and disables interaction */
  loading?: boolean;
  /** Icon to display on the start side (LTR: left, RTL: right) */
  iconStart?: React.ReactNode;
  /** Icon to display on the end side (LTR: right, RTL: left) */
  iconEnd?: React.ReactNode;
  /** Make button full width */
  fullWidth?: boolean;
  /** Children content */
  children?: React.ReactNode;
}

const LoadingSpinner: React.FC<{ size: 'sm' | 'md' | 'lg' }> = ({ size }) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-testid="loading-spinner"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      iconStart,
      iconEnd,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    // Base classes
    const baseClasses = 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    // Variant classes — accent hues ride the primary/destructive tokens so a
    // hue change (tailwind.config.js + --primary) moves every button at once.
    const variantClasses = {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-ring disabled:bg-primary-100 disabled:text-primary-800',
      secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus-visible:ring-slate-500',
      ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-500',
      danger: 'bg-destructive text-white hover:bg-red-700 focus-visible:ring-red-500',
      // Soft/reversible destructive actions (deactivate, block, stop).
      // Solid `danger` stays reserved for final actions behind a confirm.
      'danger-outline':
        'bg-transparent border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-500',
    };

    // Size classes - using logical properties for RTL support
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm rounded-md gap-1.5',
      md: 'px-4 py-2 text-base rounded-lg gap-2',
      lg: 'px-6 py-3 text-lg rounded-lg gap-2.5',
    };

    // Width classes
    const widthClasses = fullWidth ? 'w-full' : '';

    const combinedClassName = `
      ${baseClasses}
      ${variantClasses[variant]}
      ${sizeClasses[size]}
      ${widthClasses}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={combinedClassName}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {loading && <LoadingSpinner size={size} />}
        {!loading && iconStart && <span className="flex-shrink-0">{iconStart}</span>}
        {children && <span>{children}</span>}
        {!loading && iconEnd && <span className="flex-shrink-0">{iconEnd}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
